import type { FocusEventHandler, FocusEvent } from "react";
import { getGeocode } from "use-places-autocomplete";

import { GoogleAddress } from "@shared/components/AddressPulldown/GoogleAddress/GoogleAddress";
import type { Suggestion } from "@shared/hooks/useAddressAutocomplete/useAddressAutocomplete";
import type { AddressInformationData } from "@shared/types/fields/addressInformationTypes";
import type {
  BusinessAddressAttributes,
  StripePersonAttributes,
} from "@shared/types/graphqlGenerated";

import type { StatesData } from "./StatesInfo.query";

const placesAutocompleteBoilerplate = <
  T extends AddressInformationData | BusinessAddressAttributes | StripePersonAttributes,
>(
  addressInformationData: T,
  handleSetAddressInformation: (addressInfo: T) => void,
  data: StatesData | undefined
) => {
  const handleBlur =
    (onBlur: FocusEventHandler, suggestions: readonly Suggestion[]) =>
    async (e: FocusEvent<any>) => {
      const streetAddress =
        "streetAddress" in addressInformationData && addressInformationData.streetAddress;
      const address1 = "address1" in addressInformationData && addressInformationData.address1;
      onBlur(e);

      const suggestionActive = suggestions.some(suggestion => suggestion.active);
      if (suggestionActive) {
        await onSuggestionSelect(streetAddress ?? address1);
      }
    };

  const onSuggestionSelect: any = async (
    address: string,
    placeId: string,
    includeLatLng?: boolean
  ) => {
    const placeDetails = await getGeocode({ placeId });
    const streetNumber = address.split(" ")[0];
    const states = data?.viewer.states ?? [];
    const phone = "phone" in addressInformationData ? addressInformationData.phone : "";

    handleSetAddressInformation({
      phone,
      ...GoogleAddress(placeDetails, streetNumber, states, includeLatLng),
    } as T);
  };

  return { onSuggestionSelect, handleBlur };
};

export { placesAutocompleteBoilerplate };
